import { useState, type FormEvent, type ReactNode } from "react";
import { Smartphone } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/ui/Button";
import { FormError, TextField } from "@/components/ui/Field";
import { ApiError } from "@/lib/api";
import { useAuthConfig } from "@/lib/queries";
import { isStandalone } from "@/pwa/install";

function AuthLayout({ title, subtitle, children, footer }: {
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <main className="flex min-h-dvh items-center justify-center px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center gap-2">
          <img src="/favicon.svg" alt="" className="size-8" />
          <span className="text-lg font-semibold">Ledger</span>
        </div>
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-ink-3">{subtitle}</p>}
        <div className="mt-6 rounded-2xl border border-border bg-surface p-5">{children}</div>
        {footer && <p className="mt-4 text-center text-sm">{footer}</p>}
      </div>
    </main>
  );
}

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? "/";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Offer sign-up only once the server has said it is open (it enforces this regardless).
  const signUpOpen = useAuthConfig().data?.registration_open === true;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await login(email.trim(), password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not sign in. Try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      title="Sign in"
      subtitle={signUpOpen
        ? <>New here? <Link to="/register" className="font-medium text-accent hover:underline">Create an account</Link></>
        : undefined}
      // Not shown inside the installed app, where it would be pointless.
      footer={isStandalone() ? undefined : (
        <Link to="/install" className="inline-flex items-center gap-1.5 font-medium text-accent hover:underline">
          <Smartphone className="size-4" aria-hidden /> Get the app for your phone
        </Link>
      )}
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <TextField label="Email" type="email" autoComplete="email" required value={email}
                   onChange={(e) => setEmail(e.target.value)} />
        <TextField label="Password" type="password" autoComplete="current-password" required
                   value={password} onChange={(e) => setPassword(e.target.value)} />
        <FormError message={error} />
        <Button type="submit" variant="primary" size="lg" loading={busy}>Sign in</Button>
      </form>
    </AuthLayout>
  );
}

export function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ full_name: "", email: "", password: "" });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const config = useAuthConfig();

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const local: Record<string, string> = {};
    if (!form.email.includes("@")) local.email = "Enter a valid email";
    if (form.password.length < 12) local.password = "Use at least 12 characters";
    setErrors(local);
    if (Object.keys(local).length) return;
    setBusy(true);
    try {
      await register({ ...form, email: form.email.trim() });
      navigate("/", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setErrors(err.fields);
        if (!Object.keys(err.fields).length) setError(err.message);
      } else {
        setError("Could not create your account. Try again.");
      }
    } finally {
      setBusy(false);
    }
  }

  if (config.data?.registration_open === false) {
    return (
      <AuthLayout
        title="Registration is closed"
        subtitle={<>Already have an account? <Link to="/login" className="font-medium text-accent hover:underline">Sign in</Link></>}
      >
        <p className="text-sm text-ink-2">
          This Ledger is private, so new accounts can't be created here. If you should have access, ask
          the person who runs it.
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Create your account"
      subtitle={<>Already registered? <Link to="/login" className="font-medium text-accent hover:underline">Sign in</Link></>}
    >
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        <TextField label="Name" autoComplete="name" value={form.full_name}
                   onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
        <TextField label="Email" type="email" autoComplete="email" required value={form.email}
                   error={errors.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <TextField label="Password" type="password" autoComplete="new-password" required
                   value={form.password} error={errors.password}
                   hint="At least 12 characters. A passphrase works well."
                   onChange={(e) => setForm({ ...form, password: e.target.value })} />
        <FormError message={error} />
        <Button type="submit" variant="primary" size="lg" loading={busy}>Create account</Button>
      </form>
    </AuthLayout>
  );
}
